import React, { useEffect, useState } from "react";
import { Package, Clock, CheckCircle2, Truck, MessageSquare, Send } from "lucide-react";
import { useAuth } from "../context/AuthContext";
import { useApp } from "../context/AppContext";
import {
  fetchCustomerOrders,
  fetchCustomerMessages,
  sendCustomerContact,
  api,
} from "../lib/api";

/** Working-day countdown.
 * If `from` is the start date and `days` is the working-day budget, returns the
 * number of working days remaining (Mon-Fri only). 0 means "should arrive today";
 * negative means "overdue".
 */
function workingDaysLeft(fromIso, days) {
  if (!fromIso || days == null) return null;
  const start = new Date(fromIso);
  const today = new Date();
  start.setHours(0, 0, 0, 0);
  today.setHours(0, 0, 0, 0);
  let elapsed = 0;
  const cur = new Date(start);
  while (cur < today) {
    cur.setDate(cur.getDate() + 1);
    const dow = cur.getDay();
    if (dow !== 0 && dow !== 6) elapsed += 1;
  }
  return Math.max(-99, days - elapsed);
}

function StatusBadge({ status, lang }) {
  const map = {
    awaiting_payment: { en: "AWAITING PAYMENT", pt: "AGUARDA PAGAMENTO", cls: "text-kult-muted" },
    pending: { en: "PENDING", pt: "PENDENTE", cls: "text-kult-muted" },
    paid: { en: "PAID", pt: "PAGO", cls: "text-kult-yellow" },
    shipped: { en: "SHIPPED", pt: "ENVIADO", cls: "text-kult-pink" },
    delivered: { en: "DELIVERED", pt: "ENTREGUE", cls: "text-green-400" },
    cancelled: { en: "CANCELLED", pt: "CANCELADO", cls: "text-red-400" },
    refunded: { en: "REFUNDED", pt: "REEMBOLSADO", cls: "text-red-400" },
  };
  const m = map[status] || { en: status, pt: status, cls: "text-white" };
  return (
    <span className={`font-mono text-[10px] tracking-[0.3em] ${m.cls}`}>
      {lang === "pt" ? m.pt : m.en}
    </span>
  );
}

export default function CustomerPortal() {
  const { user, logout } = useAuth();
  const { lang, formatPrice } = useApp();
  const [orders, setOrders] = useState([]);
  const [messages, setMessages] = useState([]);
  const [contactForm, setContactForm] = useState({ order_number: "", message: "" });
  const [submitting, setSubmitting] = useState(false);
  const [sent, setSent] = useState(false);
  const [profile, setProfile] = useState(null); // { email_verified, saved_address }
  const [resendBusy, setResendBusy] = useState(false);
  const [resendSent, setResendSent] = useState(false);

  const reload = async () => {
    try {
      const o = await fetchCustomerOrders();
      setOrders(o.orders || []);
      const m = await fetchCustomerMessages();
      setMessages(m || []);
      const meRes = await api.get("/portal/customer/me").catch(() => null);
      if (meRes?.data) setProfile(meRes.data);
    } catch (e) { /* ignore */ }
  };

  useEffect(() => { reload(); }, []);

  const resendVerification = async () => {
    if (!user?.email) return;
    setResendBusy(true);
    setResendSent(false);
    try {
      await api.post("/auth/resend-verification", { email: user.email });
      setResendSent(true);
    } catch (_e) { /* swallow — endpoint is silent by design */ }
    setResendBusy(false);
  };

  const submitContact = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    setSent(false);
    try {
      await sendCustomerContact({
        name: user.name,
        email: user.email,
        order_number: contactForm.order_number || null,
        message: contactForm.message,
      });
      setContactForm({ order_number: "", message: "" });
      setSent(true);
      reload();
    } catch (e) { /* show subtle error */ }
    setSubmitting(false);
  };

  const fulfilled = orders.filter((o) => ["shipped", "delivered"].includes(o.status));
  const pending = orders.filter((o) => !["shipped", "delivered", "cancelled", "refunded"].includes(o.status));
  const inactive = orders.filter((o) => ["cancelled", "refunded"].includes(o.status));

  const tt = lang === "pt"
    ? {
        tag: "/ ÁREA DE CLIENTE",
        h: "OLÁ,",
        sub: "As tuas encomendas e mensagens.",
        active: "ENCOMENDAS ATIVAS",
        fulfilled: "ENCOMENDAS ENTREGUES",
        inactive: "OUTRAS",
        empty_active: "Sem encomendas ativas no momento.",
        empty_fulfilled: "Ainda não tens encomendas enviadas.",
        contact_title: "CONTACTAR-NOS",
        contact_sub: "Dúvida sobre a encomenda? Envia-nos mensagem.",
        order_no: "Nº DE ENCOMENDA (opcional)",
        message: "MENSAGEM",
        submit: "ENVIAR",
        sent_ok: "Mensagem enviada. Voltamos rapidamente.",
        my_messages: "AS MINHAS MENSAGENS",
        ships_in: "ENVIO EM",
        arrives_in: "ENTREGA EM",
        days_short: "DIAS ÚTEIS",
        today: "HOJE",
        overdue: "ATRASADO",
        logout: "SAIR",
        status: "ESTADO",
        items: "ITENS",
        total: "TOTAL",
        tracking: "RASTREIO",
      }
    : {
        tag: "/ CUSTOMER AREA",
        h: "HEY,",
        sub: "Your orders and messages.",
        active: "ACTIVE ORDERS",
        fulfilled: "DELIVERED ORDERS",
        inactive: "OTHER",
        empty_active: "No active orders right now.",
        empty_fulfilled: "No shipped orders yet.",
        contact_title: "CONTACT US",
        contact_sub: "Question about an order? Drop us a line.",
        order_no: "ORDER NUMBER (optional)",
        message: "MESSAGE",
        submit: "SEND",
        sent_ok: "Message sent. We'll get back to you.",
        my_messages: "MY MESSAGES",
        ships_in: "SHIPS IN",
        arrives_in: "ARRIVES IN",
        days_short: "WORKING DAYS",
        today: "TODAY",
        overdue: "OVERDUE",
        logout: "LOG OUT",
        status: "STATUS",
        items: "ITEMS",
        total: "TOTAL",
        tracking: "TRACKING",
      };

  const renderCountdown = (o) => {
    if (o.status === "paid" && o.ship_eta_days != null) {
      const left = workingDaysLeft(o.paid_at || o.created_at, o.ship_eta_days);
      return (
        <span className="font-mono text-[10px] tracking-[0.25em] text-kult-yellow" data-testid={`countdown-ship-${o.order_number}`}>
          {tt.ships_in} {left === 0 ? tt.today : left < 0 ? tt.overdue : `${left} ${tt.days_short}`}
        </span>
      );
    }
    if (o.status === "shipped" && o.delivery_eta_days != null) {
      const left = workingDaysLeft(o.shipped_at, o.delivery_eta_days);
      return (
        <span className="font-mono text-[10px] tracking-[0.25em] text-kult-pink" data-testid={`countdown-deliver-${o.order_number}`}>
          <Truck size={11} className="inline mr-1" />
          {tt.arrives_in} {left === 0 ? tt.today : left < 0 ? tt.overdue : `${left} ${tt.days_short}`}
        </span>
      );
    }
    return null;
  };

  const renderOrder = (o) => (
    <div key={o.order_number} className="border border-kult-border p-4 bg-kult-black" data-testid={`customer-order-${o.order_number}`}>
      <div className="flex items-start justify-between flex-wrap gap-2 mb-3">
        <div>
          <p className="font-mono text-[11px] text-white">{o.order_number}</p>
          <p className="font-mono text-[10px] text-kult-muted">{new Date(o.created_at).toLocaleDateString(lang === "pt" ? "pt-PT" : "en-GB")}</p>
        </div>
        <div className="text-right">
          <StatusBadge status={o.status} lang={lang} />
          <p className="font-display text-lg mt-1">{formatPrice(o.total || 0)}</p>
        </div>
      </div>
      <div className="space-y-1 mb-2">
        {(o.items || []).map((it, i) => (
          <p key={i} className="font-mono text-[11px] text-kult-muted">
            {it.quantity}× {it.name}{it.size ? ` · ${it.size}` : ""} <span className="text-white">{formatPrice((it.price || 0) * (it.quantity || 1))}</span>
          </p>
        ))}
      </div>
      {o.tracking_number && (
        <p className="font-mono text-[11px] text-kult-pink mt-2" data-testid={`customer-order-tracking-${o.order_number}`}>
          {tt.tracking}: <span className="text-white">{o.tracking_number}</span>
        </p>
      )}
      {renderCountdown(o)}
    </div>
  );

  return (
    <div className="max-w-[1200px] mx-auto px-5 md:px-10 py-12 md:py-20" data-testid="page-customer-portal">
      <div className="flex items-center justify-between flex-wrap gap-3 mb-2">
        <p className="font-mono text-xs tracking-[0.3em] text-kult-yellow">{tt.tag}</p>
        <button onClick={logout} className="font-mono text-[11px] tracking-[0.3em] text-kult-muted hover:text-kult-pink" data-testid="customer-logout">
          {tt.logout} →
        </button>
      </div>
      <h1 className="font-display text-5xl md:text-6xl leading-none">
        {tt.h} <span className="text-kult-pink">{user?.name?.toUpperCase()}.</span>
      </h1>
      <p className="text-kult-muted mt-2 mb-6">{tt.sub}</p>

      {/* Email verification banner — only shown when the account is unverified.
          Existing accounts (created before this feature) are marked verified
          by default so they don't see this. */}
      {profile && profile.email_verified === false && (
        <div className="border-2 border-kult-yellow bg-kult-yellow/10 p-4 mb-8 flex flex-col sm:flex-row gap-3 sm:items-center sm:justify-between" data-testid="verify-email-banner">
          <div>
            <p className="font-display text-xl text-kult-yellow leading-tight">
              {lang === "pt" ? "CONFIRMA O TEU EMAIL" : "CONFIRM YOUR EMAIL"}
            </p>
            <p className="text-kult-muted text-sm mt-1">
              {lang === "pt"
                ? `Enviámos um link de confirmação para ${user?.email}. Clica para activares totalmente a tua conta.`
                : `We sent a confirmation link to ${user?.email}. Click it to fully activate your account.`}
            </p>
          </div>
          <button
            type="button"
            onClick={resendVerification}
            disabled={resendBusy || resendSent}
            className="shrink-0 inline-flex items-center gap-2 px-4 py-2 bg-kult-yellow text-black font-mono text-xs tracking-[0.25em] hover:bg-white disabled:opacity-60"
            data-testid="verify-email-resend"
          >
            {resendSent
              ? (lang === "pt" ? "ENVIADO ✓" : "SENT ✓")
              : resendBusy
              ? (lang === "pt" ? "A ENVIAR..." : "SENDING...")
              : (lang === "pt" ? "REENVIAR LINK" : "RESEND LINK")}
          </button>
        </div>
      )}

      {/* Active orders */}
      <section className="mb-12" data-testid="customer-section-active">
        <h2 className="font-mono text-xs tracking-[0.3em] text-kult-yellow mb-4 inline-flex items-center gap-2">
          <Clock size={14} /> {tt.active} ({pending.length})
        </h2>
        {pending.length === 0 ? (
          <p className="font-mono text-xs text-kult-muted">{tt.empty_active}</p>
        ) : (
          <div className="grid gap-3 md:grid-cols-2">{pending.map(renderOrder)}</div>
        )}
      </section>

      {/* Fulfilled / delivered */}
      <section className="mb-12" data-testid="customer-section-fulfilled">
        <h2 className="font-mono text-xs tracking-[0.3em] text-kult-yellow mb-4 inline-flex items-center gap-2">
          <CheckCircle2 size={14} /> {tt.fulfilled} ({fulfilled.length})
        </h2>
        {fulfilled.length === 0 ? (
          <p className="font-mono text-xs text-kult-muted">{tt.empty_fulfilled}</p>
        ) : (
          <div className="grid gap-3 md:grid-cols-2">{fulfilled.map(renderOrder)}</div>
        )}
      </section>

      {inactive.length > 0 && (
        <section className="mb-12">
          <h2 className="font-mono text-xs tracking-[0.3em] text-kult-muted mb-4">{tt.inactive} ({inactive.length})</h2>
          <div className="grid gap-3 md:grid-cols-2">{inactive.map(renderOrder)}</div>
        </section>
      )}

      {/* Contact form */}
      <section className="mt-16 border-t border-kult-border pt-12" data-testid="customer-section-contact">
        <h2 className="font-display text-3xl mb-1">{tt.contact_title}</h2>
        <p className="text-kult-muted text-sm mb-6">{tt.contact_sub}</p>
        <form onSubmit={submitContact} className="grid md:grid-cols-2 gap-4 max-w-3xl" data-testid="customer-contact-form">
          <label className="block md:col-span-2">
            <span className="font-mono text-[10px] tracking-[0.3em] text-kult-muted block mb-2">{tt.order_no}</span>
            <input
              value={contactForm.order_number}
              onChange={(e) => setContactForm({ ...contactForm, order_number: e.target.value })}
              className="w-full bg-transparent border border-kult-border px-3 py-3 font-mono text-sm focus:border-kult-pink outline-none"
              data-testid="customer-contact-order"
              placeholder="KULT-20260101-ABC123"
            />
          </label>
          <label className="block md:col-span-2">
            <span className="font-mono text-[10px] tracking-[0.3em] text-kult-muted block mb-2">{tt.message}</span>
            <textarea
              required
              minLength={2}
              rows={5}
              value={contactForm.message}
              onChange={(e) => setContactForm({ ...contactForm, message: e.target.value })}
              className="w-full bg-transparent border border-kult-border px-3 py-3 font-mono text-sm focus:border-kult-pink outline-none"
              data-testid="customer-contact-message"
            />
          </label>
          {sent && <p className="md:col-span-2 font-mono text-xs text-kult-yellow">⚡ {tt.sent_ok}</p>}
          <button
            type="submit"
            disabled={submitting}
            className="md:col-span-2 justify-self-start bg-kult-pink text-black font-display text-xl px-8 py-3 hover:bg-kult-yellow disabled:opacity-50 inline-flex items-center gap-2"
            data-testid="customer-contact-submit"
          >
            <Send size={16} /> {submitting ? "..." : tt.submit}
          </button>
        </form>

        {messages.length > 0 && (
          <div className="mt-10">
            <h3 className="font-mono text-xs tracking-[0.3em] text-kult-yellow mb-4 inline-flex items-center gap-2">
              <MessageSquare size={14} /> {tt.my_messages}
            </h3>
            <div className="space-y-3" data-testid="customer-my-messages">
              {messages.map((m) => (
                <div key={m.id} className="border border-kult-border p-4 bg-kult-black">
                  <div className="flex items-start justify-between gap-3 mb-2">
                    <p className="font-mono text-[10px] text-kult-muted">{new Date(m.created_at).toLocaleString(lang === "pt" ? "pt-PT" : "en-GB")}</p>
                    <span className={`font-mono text-[10px] tracking-[0.25em] ${m.status === "replied" ? "text-kult-yellow" : "text-kult-muted"}`}>{m.status?.toUpperCase()}</span>
                  </div>
                  {m.order_number && <p className="font-mono text-[10px] text-kult-pink">{tt.tracking.replace(tt.tracking, "")}{m.order_number}</p>}
                  <p className="text-sm text-white whitespace-pre-line">{m.message}</p>
                  {m.admin_response && (
                    <div className="mt-3 border-l-2 border-kult-yellow pl-3">
                      <p className="font-mono text-[10px] tracking-[0.25em] text-kult-yellow mb-1">KULTOPIA</p>
                      <p className="text-sm text-white whitespace-pre-line">{m.admin_response}</p>
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}
      </section>
    </div>
  );
}
